<template>
  <v-navigation-drawer
    app
    v-model="chatStore.drawer"
    :color="$vuetify.theme.dark ? 'black' : $vuetify.breakpoint.mdAndDown ? 'white' : 'grey'"
    :floating="true"
    hide-overlay
    class="pt-2"
  >
    <v-skeleton-loader :loading="loading" type="card-heading, list-item-two-line@3" class="pa-0">
      <template v-slot:default>
        <v-list nav dense subheader>
          <v-list-item to="/explore" class="mb-0 mt-1">
            <v-list-item-icon>
              <v-icon>mdi-shimmer</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t("exploreTools") }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click="chatStore.initNewChat++" class="mb-0 mt-1">
            <v-list-item-icon>
              <v-icon>mdi-square-edit-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t("newChat") }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item-group>
            <template v-for="group in items">
              <v-subheader class="mt-4" :key="group.id">{{ group.text }}</v-subheader>
              <template v-for="chat in group.chats">
                <v-list-item :key="chat._id" class="chat-list-item" :to="`/chat/${chat._id}`">
                  <v-list-item-content>
                    <v-list-item-title>{{ chat.name }}</v-list-item-title>
                    <!-- <v-list-item-subtitle>{{ chat.mode }}</v-list-item-subtitle> -->
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-progress-circular
                      v-if="chatStore.loadingChatId === chat._id"
                      indeterminate
                      size="16"
                      color="primary"
                    ></v-progress-circular>
                    <v-icon v-else small class="ml-2 delete-chat-btn" @click.prevent="removeChat(chat._id)">
                      mdi-delete
                    </v-icon>
                  </v-list-item-action>
                </v-list-item>
              </template>
            </template>
          </v-list-item-group>
        </v-list>
      </template>
    </v-skeleton-loader>
    <ConfirmDialog ref="confirm" />
  </v-navigation-drawer>
</template>

<script>
import { useAccountStore } from "@/stores/account";
import { useEditStore } from "@/stores/edit";
import { useShortStore } from "@/stores/short";
import { useSnackbarStore } from "@/stores/snackbar";
import { useChatStore } from "@/stores/chat";
import {
  isToday,
  isYesterday,
  isThisYear,
  isWithinInterval,
  startOfToday,
  startOfYesterday,
  subDays,
  parseISO,
  format,
  getYear,
  getMonth,
} from "date-fns";
import ConfirmDialog from "@/components/ConfirmDialog.vue";

export default {
  name: "SidebarNavigation",
  components: { ConfirmDialog },
  setup() {
    const accountStore = useAccountStore();
    const editStore = useEditStore();
    const shortStore = useShortStore();
    const snackbarStore = useSnackbarStore();
    const chatStore = useChatStore();
    return { accountStore, editStore, shortStore, snackbarStore, chatStore };
  },
  data() {
    return { loading: false };
  },
  async created() {
    if (this.accountStore.isLoggedIn) await this.syncHistory();
    else this.loading = false;
  },
  computed: {
    items() {
      const chats = [...this.chatStore.chats].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      const today = startOfToday();
      const yesterday = startOfYesterday();
      const last7Days = subDays(today, 6);
      const last30Days = subDays(today, 29);

      const groups = {
        today: { id: "today", text: this.$t("today"), chats: [] },
        yesterday: { id: "yesterday", text: this.$t("yesterday"), chats: [] },
        week: { id: "prev7days", text: this.$t("previousXDays", { days: 7 }), chats: [] },
        month: { id: "prev30days", text: this.$t("previousXDays", { days: 30 }), chats: [] },
      };

      const months = {};
      const years = {};

      for (const chat of chats) {
        const date = parseISO(chat.updatedAt);
        if (isToday(date)) {
          groups.today.chats.push(chat);
        } else if (isYesterday(date)) {
          groups.yesterday.chats.push(chat);
        } else if (isWithinInterval(date, { start: last7Days, end: yesterday })) {
          groups.week.chats.push(chat);
        } else if (isWithinInterval(date, { start: last30Days, end: last7Days })) {
          groups.month.chats.push(chat);
        } else if (isThisYear(date)) {
          // Group by month
          const monthNum = getMonth(date); // 0-based
          const monthKey = String(monthNum);
          if (!months[monthKey]) {
            months[monthKey] = {
              id: `month-${monthNum + 1}`,
              text: format(date, "LLLL"), // e.g., 'April'
              chats: [],
            };
          }
          months[monthKey].chats.push(chat);
        } else {
          // Previous years: group by year
          const year = getYear(date);
          const yearKey = String(year);
          if (!years[yearKey]) {
            years[yearKey] = {
              id: `year-${year}`,
              text: String(year),
              chats: [],
            };
          }
          years[yearKey].chats.push(chat);
        }
      }

      const result = [];

      if (groups.today.chats.length) result.push(groups.today);
      if (groups.yesterday.chats.length) result.push(groups.yesterday);
      if (groups.week.chats.length) result.push(groups.week);
      if (groups.month.chats.length) result.push(groups.month);

      const thisYearMonths = Object.values(months);
      thisYearMonths.sort((a, b) => new Date(b.chats[0].updatedAt) - new Date(a.chats[0].updatedAt));
      result.push(...thisYearMonths);

      const prevYears = Object.values(years);
      prevYears.sort((a, b) => Number(b.text) - Number(a.text));
      result.push(...prevYears);

      return result;
    },
  },
  methods: {
    async syncHistory() {
      try {
        this.loading = true;
        await this.chatStore.fetchChats();
      } catch (error) {
        this.snackbarStore.text = this.$t("failedToRetrieveChats");
        this.snackbarStore.snackbar = true;
      }
      this.loading = false;
    },
    async removeChat(chatId) {
      try {
        if (await this.$refs.confirm.open(this.$t("pleaseConfirm"), this.$t("areYouSureYouWantToRemoveThisChat"))) {
          this.chatStore.loadingChatId = chatId;
          await this.chatStore.removeChat(chatId);
          if (this.$route.params.chatId === chatId) this.chatStore.initNewChat++;
        }
      } catch (err) {
        console.log(err);
        this.snackbarStore.text = this.$t("somethingWentWrong");
        this.snackbarStore.snackbar = true;
      }
      this.chatStore.loadingChatId = null;
    },
  },
};
</script>

<style scoped>
.theme--dark.v-list,
.theme--light.v-list {
  background-color: unset;
}

.v-navigation-drawer .v-skeleton-loader :deep(.v-skeleton-loader__list-item-two-line),
.v-navigation-drawer .v-skeleton-loader :deep(.v-skeleton-loader__card-heading) {
  background: none;
}

/* Hide the delete button until hover */
.chat-list-item .delete-chat-btn {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s;
}
.chat-list-item:hover .delete-chat-btn {
  opacity: 1;
  pointer-events: auto;
}
</style>
